import { useCallback } from "react";
import {
  CreateSubAssemblyRequest,
  SubAssembly,
  UpdateSubAssemblyRequest,
} from "../types/subAssembly";
import { useApiService } from "./apiService";
import {
  getSubAssemblyOffWingType,
  isAttachedSubAssembly,
  sameSubAssemblyType,
} from "../utils/subAssemblyUtils";

function mapSubAssembly(item: any): SubAssembly {
  const componentId = item.componentId ?? item.ComponentId ?? null;
  const status = item.status ?? item.Status ?? "";
  const mapped: SubAssembly = {
    id: item.id ?? item.Id,
    landingGearType: (item.landingGearType ?? item.LandingGearType ?? "").toUpperCase(),
    subAssemblyType: item.subAssemblyType ?? item.SubAssemblyType ?? "",
    serialNumber: item.serialNumber ?? item.SerialNumber ?? "",
    partNumber: item.partNumber ?? item.PartNumber ?? null,
    installDate: item.installDate ?? item.InstallDate ?? null,
    componentId,
    status,
    shopVisitReasonId: item.shopVisitReasonId ?? item.ShopVisitReasonId ?? null,
    shopVisitReasonLabel: item.shopVisitReasonLabel ?? item.ShopVisitReasonLabel ?? null,
    attachedToAirframe: item.attachedToAirframe ?? item.AttachedToAirframe ?? null,
    attachmentLocation: item.attachmentLocation ?? item.AttachmentLocation ?? null,
    airframeFamily: item.airframeFamily ?? item.AirframeFamily ?? null,
    createdDate: item.createdDate ?? item.CreatedDate,
    modifiedDate: item.modifiedDate ?? item.ModifiedDate ?? null,
    createdBy: item.createdBy ?? item.CreatedBy ?? null,
    editedBy: item.editedBy ?? item.EditedBy ?? null,
  };

  mapped.offWingType = getSubAssemblyOffWingType(mapped);
  return mapped;
}

export const useSubAssemblyService = () => {
  const apiService = useApiService();

  const getSubAssemblies = useCallback(
    async (
      landingGearType?: string,
      subAssemblyType?: string,
      attachedOnly?: boolean,
      airframeFamily?: string
    ) => {
      const params = new URLSearchParams();
      if (landingGearType && landingGearType !== "All") {
        params.set("landingGearType", landingGearType);
      }
      if (airframeFamily?.trim()) {
        params.set("airframeFamily", airframeFamily.trim());
      }
      const qs = params.toString();
      const endpoint = qs.length > 0 ? `/api/sub-assemblies?${qs}` : "/api/sub-assemblies";

      const response = await apiService.apiCall<any[]>({
        method: "GET",
        endpoint,
        requireAuth: true,
      });

      const data = Array.isArray(response) ? response : response?.data;
      let items = Array.isArray(data) ? data.map(mapSubAssembly) : [];

      if (subAssemblyType?.trim()) {
        items = items.filter((item) =>
          sameSubAssemblyType(item.subAssemblyType, subAssemblyType)
        );
      }

      if (attachedOnly === true) {
        items = items.filter((item) => isAttachedSubAssembly(item));
      } else if (attachedOnly === false) {
        items = items.filter((item) => !isAttachedSubAssembly(item));
      }

      return items;
    },
    [apiService]
  );

  const getSubAssembly = useCallback(
    async (id: string) => {
      const response = await apiService.apiCall<any>({
        method: "GET",
        endpoint: `/api/sub-assemblies/${id}`,
        requireAuth: true,
      });

      const item = response?.data ?? response;
      return item ? mapSubAssembly(item) : null;
    },
    [apiService]
  );

  const createSubAssembly = useCallback(
    async (payload: CreateSubAssemblyRequest) => {
      const response = await apiService.apiCall<any>({
        method: "POST",
        endpoint: "/api/sub-assemblies",
        payload,
        requireAuth: true,
      });

      const item = response?.data ?? response;
      return item ? mapSubAssembly(item) : null;
    },
    [apiService]
  );

  const updateSubAssembly = useCallback(
    async (id: string, payload: UpdateSubAssemblyRequest) => {
      const response = await apiService.apiCall<any>({
        method: "PUT",
        endpoint: `/api/sub-assemblies/${id}`,
        payload,
        requireAuth: true,
      });

      const item = response?.data ?? response;
      return item ? mapSubAssembly(item) : null;
    },
    [apiService]
  );

  const deleteSubAssembly = useCallback(
    async (id: string) => {
      await apiService.apiCall<void>({
        method: "DELETE",
        endpoint: `/api/sub-assemblies/${id}`,
        requireAuth: true,
      });
      return true;
    },
    [apiService]
  );

  const getLandingGearTypes = useCallback(async () => {
    const response = await apiService.apiCall<string[]>({
      method: "GET",
      endpoint: "/api/sub-assemblies/landing-gear-types",
      requireAuth: true,
    });

    const data = Array.isArray(response) ? response : response?.data;
    return Array.isArray(data) ? data : [];
  }, [apiService]);

  const getSubAssemblyTypes = useCallback(
    async (landingGearType: string, airframeFamily?: string) => {
      const params = new URLSearchParams({
        landingGearType,
      });
      if (airframeFamily?.trim()) {
        params.set("airframeFamily", airframeFamily.trim());
      }

      const response = await apiService.apiCall<string[]>({
        method: "GET",
        endpoint: `/api/sub-assemblies/sub-assembly-types?${params.toString()}`,
        requireAuth: true,
      });

      const data = Array.isArray(response) ? response : response?.data;
      return Array.isArray(data) ? data : [];
    },
    [apiService]
  );

  const getAirframeFamilies = useCallback(async () => {
    const response = await apiService.apiCall<string[]>({
      method: "GET",
      endpoint: "/api/sub-assemblies/airframe-families",
      requireAuth: true,
    });

    const data = Array.isArray(response) ? response : response?.data;
    return Array.isArray(data) ? data : [];
  }, [apiService]);

  const getLandingGearCatalog = useCallback(
    async (airframeFamily?: string) => {
      const params = new URLSearchParams();
      if (airframeFamily?.trim()) {
        params.set("airframeFamily", airframeFamily.trim());
      }

      const qs = params.toString();
      const endpoint = qs
        ? `/api/sub-assemblies/landing-gear-catalog?${qs}`
        : "/api/sub-assemblies/landing-gear-catalog";

      const response = await apiService.apiCall<any[]>({
        method: "GET",
        endpoint,
        requireAuth: true,
      });

      const data = Array.isArray(response) ? response : response?.data;
      if (!Array.isArray(data)) return [];

      return data.map((item) => ({
        positionCode: String(
          item.positionCode ?? item.PositionCode ?? item.type ?? item.Type ?? ""
        ).toUpperCase(),
        label: String(item.label ?? item.Label ?? item.type ?? item.Type ?? ""),
        allowedSubAssemblyTypes: Array.isArray(
          item.allowedSubAssemblyTypes ?? item.AllowedSubAssemblyTypes
        )
          ? (item.allowedSubAssemblyTypes ?? item.AllowedSubAssemblyTypes).map(
              (type: unknown) => String(type)
            )
          : [],
      }));
    },
    [apiService]
  );

  const getSubAssembliesForComponent = useCallback(
    async (componentId: string) => {
      const items = await getSubAssemblies();
      return items.filter(
        (item) => item.componentId === componentId && isAttachedSubAssembly(item)
      );
    },
    [getSubAssemblies]
  );

  return {
    getSubAssemblies,
    getSubAssembliesForComponent,
    getSubAssembly,
    createSubAssembly,
    updateSubAssembly,
    deleteSubAssembly,
    getLandingGearTypes,
    getSubAssemblyTypes,
    getAirframeFamilies,
    getLandingGearCatalog,
  };
};
